import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from "react";
import { useTranslation } from "react-i18next";

import { showNotification } from "@/utils/network";
import { useAuthStore } from "@/store/auth";
import {
  ChatMessageI,
  fetchChatActivity,
  fetchChatMessages,
  sendChatMessage,
  sendPetMessage,
  sendSurveyMessage,
  editChatMessage,
  deleteChatMessage,
  markChatRead,
  banUser,
} from "@/api/chat";
import {
  findWakeChimeEvent,
  playChatChime,
  playChatChimePreview,
  unlockChatSound,
} from "@/features/Chat/chatSound";

const SOUND_PREFERENCE_KEY = "fireflies-chat-sound-enabled";

interface ChatContextProps {
  messages: ChatMessageI[];
  isLoading: boolean;
  isSending: boolean;
  onlineCount: number;
  unreadCount: number;
  soundEnabled: boolean;
  toggleSound: () => void;

  input: string;
  setInput: (value: string) => void;

  isEditing: boolean;
  editingId: number | null;
  editingValue: string;
  setEditingValue: (value: string) => void;
  startEdit: (message: ChatMessageI) => void;
  cancelEdit: () => void;

  replyingTo: ChatMessageI | null;
  startReply: (message: ChatMessageI) => void;
  cancelReply: () => void;

  submit: () => Promise<void>;
  shareSurvey: (responseId: number) => Promise<void>;
  sharePet: (petId: string) => Promise<void>;
  markVisibleMessagesRead: () => Promise<void>;
  remove: (id: number) => Promise<void>;
  ban: (id: number) => Promise<void>;
}

const ChatDataContext = createContext<ChatContextProps | undefined>(undefined);

interface ChatDataProviderProps {
  children: ReactNode;
  pollIntervalMs?: number;
}

export const ChatDataProvider: React.FC<ChatDataProviderProps> = ({
  children,
  pollIntervalMs = 5000,
}) => {
  const { t } = useTranslation();
  const isAuthenticated = useAuthStore((state) =>
    Boolean(state.tokenResponse?.access)
  );

  const [messages, setMessages] = useState<ChatMessageI[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [onlineCount, setOnlineCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(
    () => localStorage.getItem(SOUND_PREFERENCE_KEY) !== "false"
  );

  const [input, setInput] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState("");
  const [replyingTo, setReplyingTo] = useState<ChatMessageI | null>(null);

  const initializedRef = useRef(false);
  const activityInFlightRef = useRef(false);
  const latestMessageIdRef = useRef(0);
  const latestMessageCreatedAtRef = useRef<string | null>(null);
  const revisionRef = useRef<string | null>(null);

  const isEditing = editingId !== null;

  const loadMessages = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        const data = await fetchChatMessages(100);
        setMessages(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error(error);
        if (!silent) {
          showNotification({ text: t("errors.generic"), type: "error" });
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [t]
  );

  const pollActivity = useCallback(async () => {
    if (activityInFlightRef.current) return;
    activityInFlightRef.current = true;

    try {
      const data = await fetchChatActivity(
        initializedRef.current ? latestMessageIdRef.current : undefined
      );
      setOnlineCount(data.online_count);
      setUnreadCount(isAuthenticated ? data.unread_count : 0);

      if (!initializedRef.current) {
        initializedRef.current = true;
        latestMessageIdRef.current = data.latest_message_id;
        latestMessageCreatedAtRef.current = data.latest_message_created_at;
        revisionRef.current = data.revision;
        return;
      }

      if (soundEnabled && data.events.length > 0) {
        const wakeEvent = findWakeChimeEvent(
          data.events,
          latestMessageCreatedAtRef.current
        );
        if (wakeEvent) void playChatChime(wakeEvent.id);
      }

      const revisionChanged = data.revision !== revisionRef.current;
      latestMessageIdRef.current = data.latest_message_id;
      latestMessageCreatedAtRef.current = data.latest_message_created_at;
      revisionRef.current = data.revision;

      if (revisionChanged) void loadMessages(true);
    } catch (error) {
      console.error(error);
    } finally {
      activityInFlightRef.current = false;
    }
  }, [isAuthenticated, loadMessages, soundEnabled]);

  useEffect(() => {
    initializedRef.current = false;
    latestMessageIdRef.current = 0;
    latestMessageCreatedAtRef.current = null;
    revisionRef.current = null;
    setUnreadCount(0);

    void loadMessages(false);
    void pollActivity();
    const intervalId = window.setInterval(pollActivity, pollIntervalMs);
    const handleFocus = () => void pollActivity();
    window.addEventListener("focus", handleFocus);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", handleFocus);
    };
  }, [isAuthenticated, loadMessages, pollActivity, pollIntervalMs]);

  useEffect(() => {
    if (!soundEnabled) return;
    const unlock = () => void unlockChatSound();
    document.addEventListener("pointerdown", unlock, { once: true });
    document.addEventListener("keydown", unlock, { once: true });
    return () => {
      document.removeEventListener("pointerdown", unlock);
      document.removeEventListener("keydown", unlock);
    };
  }, [soundEnabled]);

  const toggleSound = () => {
    setSoundEnabled((current) => {
      const next = !current;
      localStorage.setItem(SOUND_PREFERENCE_KEY, String(next));
      if (next) void playChatChimePreview();
      return next;
    });
  };

  const startEdit = (message: ChatMessageI) => {
    setReplyingTo(null);
    setEditingId(message.id);
    setEditingValue(message.text || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingValue("");
  };

  const startReply = (message: ChatMessageI) => {
    cancelEdit();
    setReplyingTo(message);
  };

  const cancelReply = () => setReplyingTo(null);

  const finishSend = async () => {
    cancelReply();
    await loadMessages(true);
    void pollActivity();
  };

  const handleSendError = (error: any) => {
    console.error(error);
    let message = t("errors.generic");
    if (error?.response?.status === 403 && error.response.data?.detail) {
      message = error.response.data.detail;
    }
    showNotification({ text: message, type: "error" });
  };

  const submit = async () => {
    const value = (isEditing ? editingValue : input).trim();
    if (!value) return;

    setSending(true);
    try {
      if (isEditing && editingId !== null) {
        await editChatMessage(editingId, value);
        cancelEdit();
      } else {
        await sendChatMessage(value, replyingTo?.id);
        setInput("");
      }
      await finishSend();
    } catch (error: any) {
      handleSendError(error);
    } finally {
      setSending(false);
    }
  };

  const shareSurvey = async (responseId: number) => {
    setSending(true);
    try {
      await sendSurveyMessage(responseId, replyingTo?.id);
      await finishSend();
    } catch (error: any) {
      handleSendError(error);
    } finally {
      setSending(false);
    }
  };

  const sharePet = async (petId: string) => {
    setSending(true);
    try {
      await sendPetMessage(petId, replyingTo?.id);
      await finishSend();
    } catch (error: any) {
      handleSendError(error);
    } finally {
      setSending(false);
    }
  };

  const markVisibleMessagesRead = useCallback(async () => {
    if (!isAuthenticated || unreadCount === 0) return;
    try {
      const result = await markChatRead(latestMessageIdRef.current);
      setUnreadCount(result.unread_count);
    } catch (error) {
      console.error(error);
    }
  }, [isAuthenticated, unreadCount]);

  const remove = async (id: number) => {
    setSending(true);
    try {
      await deleteChatMessage(id);
      if (replyingTo?.id === id) cancelReply();
      await finishSend();
    } catch (error: any) {
      handleSendError(error);
    } finally {
      setSending(false);
    }
  };

  const ban = async (id: number) => {
    setSending(true);
    try {
      await banUser(id);
      await loadMessages(true);
      showNotification({ text: t("chat.userBanned"), type: "success" });
    } catch (error: any) {
      handleSendError(error);
    } finally {
      setSending(false);
    }
  };

  return (
    <ChatDataContext.Provider
      value={{
        messages,
        isLoading: loading,
        isSending: sending,
        onlineCount,
        unreadCount,
        soundEnabled,
        toggleSound,
        input,
        setInput,
        isEditing,
        editingId,
        editingValue,
        setEditingValue,
        startEdit,
        cancelEdit,
        replyingTo,
        startReply,
        cancelReply,
        submit,
        shareSurvey,
        sharePet,
        markVisibleMessagesRead,
        remove,
        ban,
      }}
    >
      {children}
    </ChatDataContext.Provider>
  );
};

export const useChatDataContext = () => {
  const context = useContext(ChatDataContext);
  if (!context) {
    throw new Error("useChatDataContext must be used within ChatDataProvider");
  }
  return context;
};
